"use client";

import { ReactNode, RefObject, useEffect, useRef } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";
import { beginGesture, idleGesture, moveGesture } from "./pullGesture";

// The drawer overlaps the hero by 96px. The pull cap (MAX_PULL in ./pullGesture)
// must stay below this by at least the drawer's corner radius (rounded-t-2xl =
// 16px), or the rounded corners dip past the hero's clipped bottom and expose
// the background (96 - 72 = 24 > 16).
const DRAWER_OVERLAP = "-mt-24";
const SETTLE = {
  type: "spring",
  stiffness: 300,
  damping: 32
} as const;
function findScrollParent(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Mobile "scroll-over-hero" layout: the hero pins in place while the content
 * panel scrolls up over it like a drawer (rounded top).
 *
 * Two motions, kept deliberately separate:
 *
 * 1. Parallax + fade-to-white on downward scroll. A rAF loop samples the
 *    container's scrollTop (the reliable signal for a nested scroll container —
 *    iOS fires sparse scroll events but updates scrollTop every frame) and feeds
 *    it to a motion value, mapped 1:1 with `useTransform`. No smoothing spring:
 *    a spring lags then accelerates to catch up, which reads as the hero
 *    "gaining speed". The mapping is clamped to the fade window so the hero
 *    never drifts off-screen and there's nothing to catch up on scrolling back.
 *
 * 2. Pull-to-zoom on top overscroll (Airbnb stretchy header). At the very top,
 *    dragging down stretches the hero (scaled from its top edge) while the
 *    drawer rides down in lockstep, so the photo's window grows to fill the
 *    pull, then springs back on release. It tracks the finger 1:1 off the touch
 *    delta — a nested scroll container clamps scrollTop at 0, so we read the
 *    overscroll from touch rather than a negative offset, and own the gesture
 *    (preventDefault) so iOS's native bounce doesn't fight the transform.
 *
 * `drawerBadge` is an optional overlay pinned just above the drawer's top edge.
 * It rides up with the drawer (1x) and fades out in lockstep with the hero
 * whitening — positioning and fade are owned here, callers just pass content.
 */
export function ScrollOverHero({
  hero,
  children,
  heroClassName,
  className,
  scrollContainerRef,
  parallax = 0.4,
  fadeDistance = 640,
  drawerBadge,
  drawerBadgeClassName
}: {
  hero: ReactNode;
  children: ReactNode;
  heroClassName?: string;
  className?: string;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  parallax?: number;
  fadeDistance?: number;
  drawerBadge?: ReactNode;
  drawerBadgeClassName?: string;
}) {
  const stickyRef = useRef<HTMLDivElement>(null);
  const heroH = useRef(1); // cached hero height; avoids a per-frame layout read
  const reduceMotion = useReducedMotion();

  // (1) Parallax + fade — driven straight off sampled scrollTop, clamped, no spring.
  const scroll = useMotionValue(0);
  const heroY = useTransform(scroll, [0, fadeDistance], [0, -fadeDistance * parallax], {
    clamp: true
  });
  const veilOpacity = useTransform(scroll, [0, fadeDistance], [0, 1], {
    clamp: true
  });
  // Inverse of the veil: the drawer badge fades out at the exact same pace the
  // hero whitens.
  const fade = useTransform(scroll, [0, fadeDistance], [1, 0], {
    clamp: true
  });

  // (2) Top overscroll stretch (px). Clamped to >= 0 so a spring undershoot
  // can't shrink the hero below rest and reveal an edge.
  const pull = useMotionValue(0);
  const stretch = useTransform(pull, p => Math.max(0, p));
  // Scale so the hero's bottom edge moves down by exactly `stretch` px, matching
  // the drawer's shift — the window grows, the image isn't squashed.
  const heroScale = useTransform(stretch, p_0 => 1 + p_0 / heroH.current);
  useEffect(() => {
    const container = scrollContainerRef?.current ?? findScrollParent(stickyRef.current);
    if (!container) return;

    // --- (1) Parallax/fade sampler: rAF reads scrollTop while scrolling, idles out.
    let raf = 0;
    let running = false;
    let idle = 0;
    let lastY = container.scrollTop;
    const tick = () => {
      const y = container.scrollTop;
      scroll.set(y);
      const dv = y - lastY;
      lastY = y;
      if (Math.abs(dv) < 0.3) {
        if (++idle > 10) {
          running = false;
          return;
        }
      } else {
        idle = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    const kick = () => {
      if (running) return;
      running = true;
      idle = 0;
      lastY = container.scrollTop;
      raf = requestAnimationFrame(tick);
    };
    scroll.set(container.scrollTop); // initial paint
    container.addEventListener("scroll", kick, {
      passive: true
    });
    container.addEventListener("touchstart", kick, {
      passive: true
    });

    // --- (2) Pull-to-zoom (skipped under reduced motion).
    const measure = () => {
      if (stickyRef.current) heroH.current = stickyRef.current.clientHeight || 1;
    };
    let ro: ResizeObserver | undefined;
    let gesture = idleGesture();
    const atTop = () => container.scrollTop <= 0;
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      gesture = beginGesture(atTop(), t.clientX, t.clientY);
    };
    const onTouchMove = (e_0: TouchEvent) => {
      const t_0 = e_0.touches[0];
      const wasPulling = gesture.pulling;
      const res = moveGesture(gesture, atTop(), t_0.clientX, t_0.clientY);
      gesture = res.gesture;
      if (res.preventDefault) e_0.preventDefault();
      // Only touch the motion value while (or just after) pulling, so an idle
      // re-touch doesn't snap a running spring-back to 0.
      if (gesture.pulling || wasPulling) pull.set(res.pull);
    };
    const release = () => {
      const wasPulling_0 = gesture.pulling;
      gesture = idleGesture();
      if (wasPulling_0) animate(pull, 0, SETTLE);
    };
    if (!reduceMotion) {
      measure();
      ro = new ResizeObserver(measure);
      if (stickyRef.current) ro.observe(stickyRef.current);
      container.addEventListener("touchstart", onTouchStart, {
        passive: true
      });
      container.addEventListener("touchmove", onTouchMove, {
        passive: false
      });
      container.addEventListener("touchend", release, {
        passive: true
      });
      container.addEventListener("touchcancel", release, {
        passive: true
      });
    }
    return () => {
      cancelAnimationFrame(raf);
      container.removeEventListener("scroll", kick);
      container.removeEventListener("touchstart", kick);
      ro?.disconnect();
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", release);
      container.removeEventListener("touchcancel", release);
    };
  }, [scrollContainerRef, parallax, reduceMotion, scroll, pull]);
  return <>
      <div ref={stickyRef} className={cn("sticky top-0 z-0 overflow-hidden", heroClassName)}>
        <motion.div className="origin-top will-change-transform" style={{
        y: heroY,
        scale: heroScale
      }} data-sentry-element="motion.div" data-sentry-source-file="ScrollOverHero.tsx">
          {hero}
        </motion.div>
        <motion.div aria-hidden style={{
        opacity: veilOpacity
      }} className="pointer-events-none absolute inset-0 bg-white" data-sentry-element="motion.div" data-sentry-source-file="ScrollOverHero.tsx" />
      </div>
      <motion.div style={{
      y: stretch
    }} className={cn("relative z-10 rounded-t-2xl bg-gray-50 will-change-transform", DRAWER_OVERLAP, className)} data-sentry-element="motion.div" data-sentry-source-file="ScrollOverHero.tsx">
        {drawerBadge && <motion.div style={{
        opacity: fade
      }} className={cn("absolute bottom-full z-10", drawerBadgeClassName)}>
            {drawerBadge}
          </motion.div>}
        {children}
      </motion.div>
    </>;
}